import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useParams, useLocation } from 'react-router-dom';
import GoogleMapReact from 'google-map-react';
import BlockUi from 'react-block-ui';
import Api from '../../api/global.service';
import env from '../../environment.js';
import CardContainer from '../../components/card/card-container.jsx';
import BackButton from '../../components/link/back-button.jsx';
import NorthIcon from '../../components/map/north-icon.jsx';
import MapRecenterButton from '../../components/map/map-recenter-button.jsx';
import FormSelectMaterial from '../../nuup/componentes/Forms/FormSelectMaterial.jsx';
import { useCoopConfig } from '../../components/content/coop-config.jsx';
import useGeoPuntosLayer, { getGeoPuntoPositions } from '../../hooks/useGeoPuntosLayer.jsx';
import { getRiesgoProductor } from '../../constants/riesgoProductor';
import { pageStyle, prepararMapaParaImprimir, InfoCard, InfoField, PrintSimbologiaTable } from '../../components/print/print-helpers.jsx';
import {
  CircleMarker,
  PillMarker,
  safeCenter,
  getParcelaPosition,
  isValidLat,
  isValidLng,
  DEFAULT_MAP_CENTER,
  getParcelaVertices,
  fitMapToPositions,
} from './padron-mapas-list.js';

const NIVEL_SIMBOLOGIA_PATH = {
  cooperativa: 'cooperativas_simbologia',
  localidad: 'localidades_simbologia',
  cuenta: 'cuentas_simbologia',
};

// Paleta del rediseño: encabezados principales (InfoBand, bandas de grupo por
// municipio, barra de columnas de Símbología) en el verde primario de marca
// (env.colors.primary_500 = rgb(120, 157, 22)), para alinearse con el resto
// de la app; filas alternadas (zebra) en vez del estilo crema/tarjeta usado
// antes.
const BANDA_BG = env.colors.primary_500;
const ZEBRA_BG = env.colors.neutral_2;
// Encabezados "secundarios" de tabla (Clave/Comunidad/Municipio/Riesgo, etc.)
const TABLE_HEADER_BG = 'rgb(240, 224, 207)';
const TABLE_HEADER_COLOR = '#333333';

const ZOOM_COMUNIDADES_INDIVIDUALES = 13;

// Estilos de celda (no de fila): background-color en <tr> no se imprime de
// forma confiable en Chrome cuando la tabla usa border-collapse (bug conocido
// del motor de impresión), así que tanto el encabezado como el zebra se
// aplican por <td> para que sí aparezcan en el PDF.
const tableHeaderCellStyle = {
  backgroundColor: TABLE_HEADER_BG,
  color: TABLE_HEADER_COLOR,
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
};
const zebraCellStyle = i => ({
  backgroundColor: i % 2 === 1 ? ZEBRA_BG : '#fff',
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
});

const FlexTableRow = ({ children, style, className }) => (
  <div className={className} style={{ whiteSpace: 'nowrap', ...style }}>
    {children}
  </div>
);

const FlexTableCell = ({ width, bordered, children, style }) => (
  <div
    className={bordered ? 'print-cell print-cell-bordered' : 'print-cell'}
    style={{
      display: 'inline-block',
      verticalAlign: 'top',
      whiteSpace: 'normal',
      width: typeof width === 'number' ? `${width}px` : width || 'auto',
      minWidth: 0,
      padding: '8px 12px',
      fontSize: 13,
      boxSizing: 'border-box',
      border: bordered ? `1px solid ${env.colors.neutral_100}` : 'none',
      ...style,
    }}>
    {children}
  </div>
);

// Punto de riesgo (● + etiqueta) para la columna RIESGO del listado de
// comunidades — mismo color ya usado en el mapa/tabla en pantalla
// (cat_localidad_riesgo_color), solo que aquí se dibuja como texto+punto en
// vez del pill interactivo `SelectRiesgo`.
// `onDark` agrega un borde blanco al punto para que se distinga sobre la
// InfoBand verde (ej. riesgo Bajo, también verdoso).
const RiesgoDot = ({ color, label, onDark }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
    <span
      style={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: color || '#ccc',
        border: onDark ? '1px solid #fff' : 'none',
        boxSizing: 'content-box',
        display: 'inline-block',
        flexShrink: 0,
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      }}
    />
    {label || 'Sin asignar'}
  </span>
);

// Banda de datos clave (Organización/Ciclo/...) — incluida en todos los
// niveles; el nivel cooperativa la usa con 3 columnas (reemplaza las 3 cards
// Identificación/Ubicación/Superficie del diseño anterior, que el mockup no
// contempla), y localidad/cuenta con 2 (Organización/Ciclo), conservando sus
// cards existentes debajo — el mockup solo definió el nivel cooperativa.
const InfoBand = ({ items }) => (
  <div
    className="print-avoid-break"
    style={{
      display: 'flex',
      backgroundColor: BANDA_BG,
      borderRadius: 4,
      marginBottom: 16,
      overflow: 'hidden',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    }}>
    {items.map((item, i) => (
      <div key={i} style={{ flex: 1, padding: '10px 16px', textAlign: 'center' }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)' }}>
          {item.label}
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginTop: 2 }}>
          {item.value === 0 || item.value ? item.value : '—'}
        </div>
      </div>
    ))}
  </div>
);

// Vista de impresión de Padrón > Mapas — mismo principio que
// parcelas-profile-print.js (página aparte, no un print() del contenido en
// pantalla) pero dinámica por nivel: cooperativa/comunidad/productor muestran
// datos, mapa y símbología distintos. Los datos de identificación (nombre,
// municipio, listas ya cargadas, conteos) viajan por location.state — este
// componente NO los vuelve a pedir, ya que padron-mapas-list.js ya los tiene
// en memoria al momento de pulsar "Exportar PDF" (mismo criterio que
// padron-mapas-edit-simbologia.js usa para su título). Solo la símbología del
// nivel se pide de nuevo, para que la hoja siempre refleje lo último guardado.
const PadronMapasPrint = ({ nivel }) => {
  const componentRef = useRef(null);
  const mapWrapperRef = useRef(null);
  const restaurarMapaRef = useRef(null);
  const { ownerUuid } = useParams();
  const location = useLocation();
  const coopConfig = useCoopConfig();
  const state = location.state || {};

  const [blocking, setBlocking] = useState(false);
  const [geoPuntos, setGeoPuntos] = useState([]);
  const [mapType, setMapType] = useState('terrain');
  const [map, setMap] = useState(null);
  const [maps, setMaps] = useState(null);
  const { legendData } = useGeoPuntosLayer(map, maps, geoPuntos);

  const anchorCenter = safeCenter(state.center || DEFAULT_MAP_CENTER);
  const anchorZoom = state.zoom || 12;
  const [liveZoom, setLiveZoom] = useState(anchorZoom);

  const dataLocalidades = state.dataLocalidades || [];
  // Una cooperativa puede tener comunidades repartidas en más de un estado —
  // se listan todos, sin repetir, concatenados (ej. "Puebla - Chiapas").
  const estadosCooperativa = [...new Set(dataLocalidades.map(loc => loc.estado).filter(Boolean))].join(' - ');
  const localidad = state.localidad || null;
  // Riesgo de la comunidad (Nivel 2): se prefiere la llave dedicada
  // `state.riesgoLocalidad` ({ id, label, color }) que manda la lista con el
  // valor ya editado en pantalla; si no viene, se cae a los campos
  // cat_localidad_riesgo* de `localidad` (mismos que usa el Nivel 1).
  const riesgoLocalidad = {
    id: state.riesgoLocalidad?.id ?? localidad?.id_cat_localidad_riesgo ?? null,
    label: state.riesgoLocalidad?.label ?? localidad?.cat_localidad_riesgo ?? null,
    color: state.riesgoLocalidad?.color ?? localidad?.cat_localidad_riesgo_color ?? null,
  };
  const dataProductoresLocalidad = state.dataProductoresLocalidad || [];
  const productorInfo = state.productorInfo || null;
  const parcelasUnidad = state.parcelasUnidad || [];
  const totalAreaUnidad = state.totalAreaUnidad || 0;

  const nombreCooperativa = coopConfig.config?.cooperativa?.nombre || 'Cooperativa';
  const cicloActual = coopConfig.activeCicloSIC?.()?.nombre || '—';
  const fechaHoy = new Date().toLocaleDateString('es-MX');

  const nameToPrint =
    nivel === 'cooperativa'
      ? `MapaDeComunidades_${nombreCooperativa}`
      : nivel === 'localidad'
      ? `MapaDe_${localidad?.localidad || 'Comunidad'}`
      : `UnidadDeProduccion_${productorInfo?.productor_nombre || ''}`;

  const reactToPrintContent = useCallback(() => componentRef.current, []);

  // Deshace el ajuste temporal que prepararMapaParaImprimir le hizo al mapa.
  const restaurarMapa = () => {
    if (restaurarMapaRef.current) restaurarMapaRef.current();
    restaurarMapaRef.current = null;
  };

  // Sin onAfterPrint: el evento "afterprint" del navegador se dispara igual
  // si el usuario imprime o si cancela el diálogo (no hay forma de distinguir
  // los dos casos desde JS), así que navegar de vuelta ahí regresaba al
  // usuario a la lista incluso al cancelar. El botón "Volver" ya cubre la
  // salida manual de esta pantalla.
  const handlePrint = useReactToPrint({
    content: reactToPrintContent,
    documentTitle: nameToPrint,
    removeAfterPrint: true,
    copyStyles: true,
    pageStyle,
    // Ajusta el mapa al ancho de la hoja y espera sus mosaicos antes de que
    // react-to-print copie el DOM al iframe de impresión.
    onBeforeGetContent: () =>
      prepararMapaParaImprimir(mapWrapperRef.current, map, maps).then(restaurar => {
        restaurarMapaRef.current = restaurar;
      }),
    // Para este punto el DOM ya se copió al iframe: se puede devolver el mapa
    // en pantalla a su ancho normal.
    onBeforePrint: restaurarMapa,
    onPrintError: restaurarMapa,
  });

  // Símbología siempre fresca desde el backend (no viaja por location.state)
  // para que la hoja impresa refleje lo último guardado en Editar Simbología.
  useEffect(() => {
    setBlocking(true);
    const request = nivel === 'cooperativa' ? Api.getAll(NIVEL_SIMBOLOGIA_PATH[nivel]) : Api.get(NIVEL_SIMBOLOGIA_PATH[nivel], ownerUuid);
    request
      .then(response => setGeoPuntos(response.data || []))
      .catch(() => setGeoPuntos([]))
      .finally(() => setBlocking(false));
  }, [nivel, ownerUuid]);

  const renderMap = (m, ms) => {
    setMap(m);
    setMaps(ms);
  };

  const panTo = pos => {
    if (map) map.panTo(pos);
  };

  // Encuadra el mapa para mostrar todo lo del nivel (mismo criterio que los
  // botones de padron-mapas-list.js) más los puntos de simbología.
  const recenterMap = () => {
    const positions = [];
    if (nivel === 'cooperativa') {
      dataLocalidades
        .filter(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud))
        .forEach(loc => positions.push({ lat: Number(loc.localidad_latitud), lng: Number(loc.localidad_longitud) }));
    }
    if (nivel === 'localidad') {
      if (isValidLat(localidad?.localidad_latitud) && isValidLng(localidad?.localidad_longitud)) {
        positions.push({ lat: Number(localidad.localidad_latitud), lng: Number(localidad.localidad_longitud) });
      }
      dataProductoresLocalidad.forEach(productor => positions.push(getParcelaPosition(productor)));
    }
    if (nivel === 'cuenta') parcelasUnidad.forEach(parcela => positions.push(...getParcelaVertices(parcela)));
    positions.push(...getGeoPuntoPositions(geoPuntos));
    fitMapToPositions(map, maps, positions);
  };

  const coordsCenter = coords => {
    const lat = coords.reduce((s, c) => s + c.lat, 0) / coords.length;
    const lng = coords.reduce((s, c) => s + c.lng, 0) / coords.length;
    return { lat, lng };
  };

  // Dibuja los polígonos de parcelas (Nivel productor) — mismo trazo que ya
  // se usa en pantalla en padron-mapas-list.js, para que el mapa impreso no
  // se vea "vacío" de polígonos frente a lo que el usuario ya veía al pulsar
  // "Exportar PDF". Nivel comunidad ya no los dibuja: con muchas parcelas
  // cercanas se veían encimados y sin datos claros (decisión confirmada con
  // el usuario).
  useEffect(() => {
    if (!map || !maps) return;
    const source = nivel === 'cuenta' ? parcelasUnidad : [];
    const drawn = source
      .filter(p => p.poligono)
      .map(p => {
        const poligono = JSON.parse(p.poligono);
        const path = poligono.coordinates[0].map(pt => ({ lat: pt[1], lng: pt[0] }));
        const color = p.cuenta_categoria_color || '#91B833';
        const polygon = new maps.Polygon({
          paths: path,
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.25,
        });
        polygon.setMap(map);
        return polygon;
      });
    return () => drawn.forEach(polygon => polygon.setMap(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, maps, nivel]);

  const tituloNivel =
    nivel === 'cooperativa'
      ? 'Mapa de Comunidades'
      : nivel === 'localidad'
      ? 'Mapa de Productores y Parcelas por Comunidad'
      : `Mapa de Unidad de Producción - ${productorInfo?.productor_nombre || ''}`;

  const subtituloNivel =
    nivel === 'cooperativa'
      ? 'Ubicación de localidades que participan en la organización'
      : nivel === 'localidad'
      ? `Se muestran todas las parcelas de los productores ubicados en ${localidad?.localidad || 'la Comunidad'}`
      : 'Detalle de la unidad de producción y sus parcelas';

  // Agrupa las comunidades por municipio (dato real ya existente en cada
  // registro) — reemplaza la agrupación por "punto/zona" del mockup, que no
  // corresponde a ningún dato real hoy (ver aclaración con el usuario).
  const comunidadesPorMunicipio = useMemo(() => {
    const groups = {};
    dataLocalidades.forEach(loc => {
      const key = loc.municipio || 'Sin municipio';
      if (!groups[key]) groups[key] = [];
      groups[key].push(loc);
    });
    return Object.entries(groups).map(([municipio, items]) => ({ municipio, items }));
  }, [dataLocalidades]);

  const municipioMarkers = useMemo(
    () =>
      comunidadesPorMunicipio
        .map((group, gi) => {
          const conCoordenadas = group.items.filter(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud));
          if (!conCoordenadas.length) return null;
          const lat = conCoordenadas.reduce((acc, loc) => acc + Number(loc.localidad_latitud), 0) / conCoordenadas.length;
          const lng = conCoordenadas.reduce((acc, loc) => acc + Number(loc.localidad_longitud), 0) / conCoordenadas.length;
          return { lat, lng, letra: String.fromCharCode(65 + (gi % 26)), municipio: group.municipio, count: group.items.length };
        })
        .filter(Boolean),
    [comunidadesPorMunicipio]
  );

  // Agrupa las filas por parcela (dataProductoresLocalidad) en una fila por
  // productor — tabla plana "Productores por Parcela (N)" del Nivel Comunidad.
  const productoresLocalidad = useMemo(() => {
    const map = new Map();
    dataProductoresLocalidad.forEach(p => {
      if (!p.productor_uuid) return;
      if (!map.has(p.productor_uuid)) {
        map.set(p.productor_uuid, {
          productor_uuid: p.productor_uuid,
          productor_id: p.productor_id,
          productor_nombre: p.productor_nombre,
          cuenta_categoria: p.cuenta_categoria,
          riesgo: getRiesgoProductor(p),
          parcelas: [],
        });
      }
      map.get(p.productor_uuid).parcelas.push(p);
    });
    return Array.from(map.values());
  }, [dataProductoresLocalidad]);
  const totalParcelasLocalidad = productoresLocalidad.reduce((acc, p) => acc + p.parcelas.length, 0);

  return (
    <div>
      <BlockUi tag="div" message="" blocking={blocking} renderChildren={true}>
        {/* Screen-only controls */}
        <div className="no-print">
          <div className="d-flex align-items-center mt-2 mb-2 justify-content-between">
            <BackButton label="Volver" goBackDefault="/admin/padron/mapas/list" />
            <button
              onClick={handlePrint}
              className="btn btn-sirio ml-2"
              style={{ borderRadius: '20px' }}
              type="button"
              aria-label="Imprimir mapa">
              <i className="fa fa-print mr-1" aria-hidden="true"></i> Imprimir
            </button>
          </div>

          <div className="row align-items-center mb-3">
            <div className="col-md-8">
              <div
                className="d-flex align-items-start p-3"
                style={{
                  backgroundColor: env.colors.neutral_2,
                  borderRadius: 12,
                  color: env.colors.neutral_700,
                  fontSize: 14,
                }}>
                <i className="fa fa-info-circle mr-2" aria-hidden="true" style={{ color: env.colors.primary_500, marginTop: 3 }}></i>
                <span>
                  <span className="font-weight-bold">Nota:</span> Para mejores resultados al imprimir te sugerimos ajustar el zoom y la
                  posición del mapa.
                </span>
              </div>
            </div>
            <div className="col-md-4">
              <FormSelectMaterial
                id="tipoMapa"
                error={false}
                label="Tipo Mapa"
                value={mapType}
                onChange={v => setMapType(v)}
                height={36}
                options={[
                  { value: 'roadmap', label: 'Plano' },
                  { value: 'terrain', label: 'Terreno' },
                  { value: 'satellite', label: 'Satelital' },
                  { value: 'hybrid', label: 'Híbrido' },
                ]}
                showAll={false}
              />
            </div>
          </div>
        </div>

        {/* Printable content */}
        <CardContainer style={{ padding: '24px 28px' }}>
          <div ref={componentRef} className="print-root">
            <div className="print-fixed-header" style={{ display: 'none' }}>
              <img src={env.images.logo} alt="Sirio" style={{ height: 22 }} />
              <span style={{ fontSize: 10, color: env.colors.neutral_500 }}>{fechaHoy}</span>
            </div>

            <div className="print-page print-page-first">
              <div style={{ marginBottom: 16 }}>
                <h2 style={{ textAlign: 'left', fontSize: 22, fontWeight: 700, color: env.colors.neutral_900, margin: 0 }}>
                  {tituloNivel}
                </h2>
                <div style={{ fontSize: 13, color: env.colors.neutral_500, marginTop: 2 }}>{subtituloNivel}</div>
              </div>

              {nivel === 'cooperativa' && (
                <InfoBand
                  items={[
                    { label: 'Organización', value: nombreCooperativa },
                    { label: 'Estado', value: estadosCooperativa },
                    { label: 'Ciclo', value: cicloActual },
                  ]}
                />
              )}
              {nivel === 'localidad' && (
                <InfoBand
                  items={[
                    { label: 'Organización', value: nombreCooperativa },
                    { label: 'Comunidad', value: localidad?.localidad },
                    { label: 'Clave', value: localidad?.numero },
                    { label: 'Riesgo', value: <RiesgoDot color={riesgoLocalidad.color} label={riesgoLocalidad.label} onDark /> },
                    { label: 'Ciclo', value: cicloActual },
                  ]}
                />
              )}
              {nivel === 'cuenta' && (
                <InfoBand
                  items={[
                    { label: 'Organización', value: nombreCooperativa },
                    { label: 'Ciclo', value: cicloActual },
                  ]}
                />
              )}

              {/* Datos agrupados en cards, distintos por nivel — Cooperativa ya
                  no las usa (su detalle ahora vive en "Listado de Comunidades"
                  más abajo, agrupado por municipio); Comunidad tampoco (su
                  detalle de identificación/ubicación queda cubierto por la
                  InfoBand de arriba y el mapa + símbología). */}
              <div className="row print-info-cards">
                {nivel === 'cuenta' && (
                  <>
                    <InfoCard title="Identificación">
                      <InfoField label="Productor" value={productorInfo?.productor_nombre} />
                      <InfoField label="Código/Clave" value={productorInfo?.productor_id} mono />
                      <InfoField label="Categoría" value={productorInfo?.cuenta_categoria} />
                    </InfoCard>
                    <InfoCard title="Ubicación">
                      <InfoField label="Comunidad" value={localidad?.localidad} />
                      <InfoField label="Municipio" value={localidad?.municipio} />
                    </InfoCard>
                    <InfoCard title="Superficie">
                      <InfoField label="Total de parcelas" value={parcelasUnidad.length} mono />
                      <InfoField
                        label="Área total (ha)"
                        mono
                        value={totalAreaUnidad.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      />
                    </InfoCard>
                  </>
                )}
              </div>

              {/* Mapa a todo lo ancho */}
              <div
                ref={mapWrapperRef}
                className="map-rounded print-map-wrapper"
                style={{ position: 'relative', height: 540, width: '100%', marginBottom: 8 }}>
                <NorthIcon color={mapType === 'satellite' || mapType === 'hybrid' ? 'white' : 'black'} />
                {map && <MapRecenterButton onClick={recenterMap} />}
                <GoogleMapReact
                  bootstrapURLKeys={{ key: env.googleApiKey }}
                  options={() => ({ mapTypeId: mapType, rotateControl: true, scaleControl: true })}
                  center={anchorCenter}
                  zoom={liveZoom}
                  onChange={({ zoom }) => setLiveZoom(zoom)}
                  yesIWantToUseGoogleMapApiInternals
                  onGoogleApiLoaded={({ map, maps }) => renderMap(map, maps)}>
                  {nivel === 'cooperativa' &&
                    (liveZoom >= ZOOM_COMUNIDADES_INDIVIDUALES
                      ? dataLocalidades
                          .filter(loc => isValidLat(loc.localidad_latitud) && isValidLng(loc.localidad_longitud))
                          .map((loc, i) => (
                            <CircleMarker
                              key={i}
                              lat={Number(loc.localidad_latitud)}
                              lng={Number(loc.localidad_longitud)}
                              number={loc.numero}
                              color={loc.cat_localidad_riesgo_color}
                              title={loc.localidad}
                            />
                          ))
                      : municipioMarkers.map((group, i) => (
                          <CircleMarker
                            key={i}
                            lat={group.lat}
                            lng={group.lng}
                            number={group.letra}
                            color={BANDA_BG}
                            title={`${group.municipio} · ${group.count} comunidad${group.count === 1 ? '' : 'es'}`}
                          />
                        )))}

                  {nivel === 'localidad' && isValidLat(localidad?.localidad_latitud) && isValidLng(localidad?.localidad_longitud) && (
                    <CircleMarker
                      lat={Number(localidad.localidad_latitud)}
                      lng={Number(localidad.localidad_longitud)}
                      number=""
                      color="#000"
                      size={14}
                      title={localidad.localidad}
                    />
                  )}
                  {nivel === 'localidad' &&
                    dataProductoresLocalidad
                      .map(productor => ({ ...productor, _pos: getParcelaPosition(productor) }))
                      .filter(productor => productor._pos)
                      .map((productor, i) => (
                        <PillMarker
                          key={i}
                          lat={productor._pos.lat}
                          lng={productor._pos.lng}
                          label={productor.productor_id}
                          color={productor.cuenta_categoria_color}
                          title={productor.productor_nombre}
                        />
                      ))}

                  {nivel === 'cuenta' &&
                    parcelasUnidad
                      .map(p => ({ ...p, _pos: getParcelaPosition(p) }))
                      .filter(p => p._pos)
                      .map((p, i) => (
                        <PillMarker
                          key={i}
                          lat={p._pos.lat}
                          lng={p._pos.lng}
                          label={p.parcela_nombre}
                          color={p.cuenta_categoria_color}
                          title={p.parcela_nombre}
                        />
                      ))}
                </GoogleMapReact>
              </div>

              {/* SÍMBOLOGÍA */}
              <PrintSimbologiaTable
                legendData={legendData}
                panTo={panTo}
                coordsCenter={coordsCenter}
                bodyBg="#fff"
                titleOutside
                borderColor={env.colors.neutral_100}
                columnHeaderBg={BANDA_BG}
                extraPoint={
                  nivel === 'localidad' && isValidLat(localidad?.localidad_latitud) && isValidLng(localidad?.localidad_longitud)
                    ? {
                        label: localidad?.localidad || 'Comunidad',
                        color: '#000',
                        onClick: () => panTo({ lat: Number(localidad.localidad_latitud), lng: Number(localidad.localidad_longitud) }),
                      }
                    : null
                }
              />
            </div>

            {/* Productores de la comunidad — tabla plana (sin agrupar en
                "puntos"/zonas: no existe ese clustering en los datos hoy;
                decisión confirmada con el usuario), fluye tras la símbología. */}
            {nivel === 'localidad' && productoresLocalidad.length > 0 && (
              <div style={{ marginTop: 16 }}>
                <div className="print-avoid-break">
                  <h4 style={{ marginBottom: 4 }}>Listado de productores y sus parcelas</h4>
                  <div style={{ fontSize: 13, color: env.colors.neutral_500, marginBottom: 10 }}>
                    Se muestran {productoresLocalidad.length} productores y {totalParcelasLocalidad} parcelas que pertenecen a esta
                    Comunidad
                  </div>
                  <FlexTableRow style={{ fontWeight: 700 }}>
                    <FlexTableCell width={81} style={tableHeaderCellStyle}>
                      Código
                    </FlexTableCell>
                    <FlexTableCell width="calc(100% - 444px)" style={tableHeaderCellStyle}>
                      Productor
                    </FlexTableCell>
                    <FlexTableCell width={91} style={tableHeaderCellStyle}>
                      # Parcelas
                    </FlexTableCell>
                    <FlexTableCell width={141} style={tableHeaderCellStyle}>
                      Categoría
                    </FlexTableCell>
                    <FlexTableCell width={131} style={tableHeaderCellStyle}>
                      Riesgo
                    </FlexTableCell>
                  </FlexTableRow>
                </div>
                <div style={{ width: '100%', marginTop: 6 }}>
                  {productoresLocalidad.map((p, i) => (
                    <FlexTableRow key={i} className="print-avoid-break">
                      <FlexTableCell width={81} style={zebraCellStyle(i)}>
                        {p.productor_id}
                      </FlexTableCell>
                      <FlexTableCell width="calc(100% - 444px)" style={zebraCellStyle(i)}>
                        {p.productor_nombre}
                      </FlexTableCell>
                      <FlexTableCell width={91} style={zebraCellStyle(i)}>
                        {p.parcelas.length}
                      </FlexTableCell>
                      <FlexTableCell width={141} style={zebraCellStyle(i)}>
                        {p.cuenta_categoria || 'Sin asignar'}
                      </FlexTableCell>
                      <FlexTableCell width={131} style={zebraCellStyle(i)}>
                        <RiesgoDot color={p.riesgo.color} label={p.riesgo.label} />
                      </FlexTableCell>
                    </FlexTableRow>
                  ))}
                </div>
              </div>
            )}

            {nivel === 'cuenta' && (
              <div style={{ marginTop: 16 }}>
                <div className="print-avoid-break">
                  <h4 style={{ marginBottom: 10 }}>Parcelas de la unidad de producción</h4>
                  <FlexTableRow style={{ fontWeight: 700 }}>
                    <FlexTableCell width="33.333%" style={tableHeaderCellStyle}>
                      Parcela
                    </FlexTableCell>
                    <FlexTableCell width="33.333%" style={tableHeaderCellStyle}>
                      Nombre
                    </FlexTableCell>
                    <FlexTableCell width="33.334%" style={tableHeaderCellStyle}>
                      Área
                    </FlexTableCell>
                  </FlexTableRow>
                </div>
                <div style={{ width: '100%', marginTop: 6 }}>
                  {parcelasUnidad.length > 0 ? (
                    parcelasUnidad.map((p, i) => (
                      <FlexTableRow key={i} className="print-avoid-break">
                        <FlexTableCell width="33.333%" style={zebraCellStyle(i)}>
                          {p.id_parcela}
                        </FlexTableCell>
                        <FlexTableCell width="33.333%" style={zebraCellStyle(i)}>
                          {p.parcela_nombre}
                        </FlexTableCell>
                        <FlexTableCell width="33.334%" style={zebraCellStyle(i)}>
                          {!isNaN(parseFloat(p.parcela_superficie))
                            ? `${parseFloat(p.parcela_superficie).toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })} ha`
                            : '—'}
                        </FlexTableCell>
                      </FlexTableRow>
                    ))
                  ) : (
                    <FlexTableRow>
                      <FlexTableCell width="100%" style={{ textAlign: 'center', color: env.colors.neutral_500 }}>
                        Sin parcelas registradas
                      </FlexTableCell>
                    </FlexTableRow>
                  )}
                </div>
              </div>
            )}

            {/* Listado de Comunidades agrupado por municipio — solo Cooperativa;
                reemplaza la agrupación por "punto/zona" del mockup (ver nota al
                inicio del archivo) por la agrupación real disponible. La letra
                del badge es solo un índice visual de grupo (A, B, C...), no
                implica ningún "punto" geográfico — el texto de la barra sí
                muestra el dato real (municipio). Sin bordes de celda (solo el
                contraste de zebra) para que no se vean separadores. */}
            {nivel === 'cooperativa' && (
              <div style={{ marginTop: 16 }}>
                <h4 style={{ marginBottom: 4 }}>Listado de Comunidades</h4>
                <div style={{ fontSize: 13, color: env.colors.neutral_500, marginBottom: 10 }}>
                  Se muestran {dataLocalidades.length} comunidades agrupadas por municipio para facilitar la lectura del mapa
                </div>
                {comunidadesPorMunicipio.map((group, gi) => (
                  <div key={gi} style={{ marginBottom: 18 }}>
                    <div className="print-avoid-break">
                      <div
                        style={{
                          backgroundColor: BANDA_BG,
                          color: '#fff',
                          padding: '8px 12px',
                          borderRadius: 10,
                          marginBottom: 8,
                          WebkitPrintColorAdjust: 'exact',
                          printColorAdjust: 'exact',
                        }}>
                        <span
                          style={{
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            backgroundColor: '#fff',
                            color: BANDA_BG,
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            textAlign: 'center',
                            lineHeight: '26px',
                            fontWeight: 700,
                            fontSize: 13,
                          }}>
                          {String.fromCharCode(65 + (gi % 26))}
                        </span>
                        <span style={{ fontWeight: 700, fontSize: 13, verticalAlign: 'middle', marginLeft: 10 }}>
                          {group.municipio} · {group.items.length} comunidad{group.items.length === 1 ? '' : 'es'}
                        </span>
                      </div>
                      <FlexTableRow style={{ fontWeight: 700 }}>
                        <FlexTableCell width={81} style={tableHeaderCellStyle}>
                          Clave
                        </FlexTableCell>
                        <FlexTableCell width="calc((100% - 242px) / 2)" style={tableHeaderCellStyle}>
                          Comunidad
                        </FlexTableCell>
                        <FlexTableCell width="calc((100% - 242px) / 2)" style={tableHeaderCellStyle}>
                          Municipio
                        </FlexTableCell>
                        <FlexTableCell width={161} style={tableHeaderCellStyle}>
                          Riesgo
                        </FlexTableCell>
                      </FlexTableRow>
                    </div>
                    <div style={{ width: '100%' }}>
                      {group.items.map((loc, i) => (
                        <FlexTableRow key={i} className="print-avoid-break">
                          <FlexTableCell width={81} style={zebraCellStyle(i)}>
                            {String(loc.numero).padStart(2, '0')}
                          </FlexTableCell>
                          <FlexTableCell width="calc((100% - 242px) / 2)" style={zebraCellStyle(i)}>
                            {loc.localidad}
                          </FlexTableCell>
                          <FlexTableCell width="calc((100% - 242px) / 2)" style={zebraCellStyle(i)}>
                            {loc.municipio}
                          </FlexTableCell>
                          <FlexTableCell width={161} style={zebraCellStyle(i)}>
                            <RiesgoDot color={loc.cat_localidad_riesgo_color} label={loc.cat_localidad_riesgo} />
                          </FlexTableCell>
                        </FlexTableRow>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="print-fixed-footer" style={{ display: 'none' }}>
              <span style={{ fontSize: 9, color: env.colors.neutral_400 }}>
                {nombreCooperativa} · {cicloActual}
              </span>
            </div>
          </div>
        </CardContainer>
      </BlockUi>
    </div>
  );
};

export default PadronMapasPrint;
